import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Button, Form, Badge, Alert, Card, Spinner } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import { ReconciliationMatchModal } from './ReconciliationMatchModal';

export function ReconciliationView({ apiCall, user }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  // Store filter defaults to the user's own store (switchable to any / all).
  const [stores, setStores] = useState([]);
  const [filterStore, setFilterStore] = useState(user?.storeId ? String(user.storeId) : '');

  // Match modal
  const [matchModalPayment, setMatchModalPayment] = useState(null);

  // DLQ
  const [dlqMessages, setDlqMessages] = useState([]);
  const [dlqCount, setDlqCount] = useState(0);
  const [showDlq, setShowDlq] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (filterType) params.set('payment_type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      if (filterStore) params.set('store', filterStore);

      const result = await apiCall(`${API_BASE_URL}/reconciliation/extracted?${params.toString()}`);
      setPayments(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load extracted payments');
    } finally {
      setLoading(false);
    }
  }, [apiCall, offset, filterType, filterStatus, filterStore]);

  const fetchStores = useCallback(async () => {
    try {
      const result = await apiCall(`${API_BASE_URL}/stores`);
      setStores(result.stores || result.data || []);
    } catch {
      // Store list is non-critical; the filter just falls back to "All Stores".
    }
  }, [apiCall]);

  const fetchDlq = useCallback(async () => {
    try {
      const result = await apiCall(`${API_BASE_URL}/reconciliation/dlq`);
      const messages = result.data?.messages || result.data || [];
      setDlqMessages(Array.isArray(messages) ? messages : []);
      setDlqCount(Array.isArray(messages) ? messages.length : 0);
    } catch {
      // DLQ fetch is non-critical
    }
  }, [apiCall]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchDlq();
  }, [fetchDlq]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleDismiss = async (id) => {
    try {
      await apiCall(`${API_BASE_URL}/reconciliation/${id}/dismiss`, { method: 'POST', body: JSON.stringify({}) });
      fetchPayments();
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };

  const handleDeleteDlq = async (receiptHandle) => {
    if (!window.confirm('Delete this DLQ message?')) return;
    try {
      await apiCall(`${API_BASE_URL}/reconciliation/dlq/${encodeURIComponent(receiptHandle)}`, { method: 'DELETE' });
      fetchDlq();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleMatchComplete = () => {
    setMatchModalPayment(null);
    fetchPayments();
  };

  const getStatusBadge = (status) => {
    const variants = { pending: 'warning', matched: 'success', dismissed: 'secondary' };
    return <Badge bg={variants[status] || 'light'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = { zelle: 'primary', cash_app: 'success' };
    const labels = { zelle: 'Zelle', cash_app: 'Cash App' };
    return <Badge bg={variants[type] || 'secondary'}>{labels[type] || type}</Badge>;
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <>
      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Select size="sm" value={filterStore} onChange={(e) => { setFilterStore(e.target.value); setOffset(0); }}>
            <option value="">All Stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select size="sm" value={filterType} onChange={(e) => { setFilterType(e.target.value); setOffset(0); }}>
            <option value="">All Types</option>
            <option value="zelle">Zelle</option>
            <option value="cash_app">Cash App</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select size="sm" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="matched">Matched</option>
            <option value="dismissed">Dismissed</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button variant="outline-secondary" size="sm" onClick={fetchPayments}>
            Refresh
          </Button>
        </Col>
        <Col md={3} className="text-end">
          {dlqCount > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowDlq(!showDlq)}
            >
              DLQ <Badge bg="danger">{dlqCount}</Badge>
            </Button>
          )}
        </Col>
      </Row>

      {/* DLQ Section */}
      {showDlq && dlqMessages.length > 0 && (
        <Alert variant="danger" className="mb-3">
          <Alert.Heading>Failed Extractions (DLQ)</Alert.Heading>
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Error</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dlqMessages.map((msg, idx) => (
                <tr key={idx}>
                  <td><small>{msg.error || msg.body || 'Unknown error'}</small></td>
                  <td><small>{msg.received_at || '-'}</small></td>
                  <td>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteDlq(msg.receipt_handle)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {total > 0 && (
            <div className="px-3 py-2 bg-light">
              <small className="text-muted">
                Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} extracted payments
              </small>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading...</div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Store</th>
                  <th>Sender</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Memo</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">No extracted payments found</td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr key={p.id}>
                      <td>{p.payment_date ? p.payment_date.split('T')[0] : '-'}</td>
                      <td>
                        {p.store_name
                          ? <Badge bg="info">{p.store_name}</Badge>
                          : <small className="text-muted" title={p.recipient_email || ''}>{p.recipient_email || '-'}</small>}
                      </td>
                      <td>{p.sender_name}</td>
                      <td>${parseFloat(p.amount).toFixed(2)}</td>
                      <td>{getTypeBadge(p.payment_type)}</td>
                      <td><small>{p.memo || '-'}</small></td>
                      <td>{getStatusBadge(p.status)}</td>
                      <td>
                        {p.status === 'pending' && (
                          <>
                            <Button variant="primary" size="sm" className="me-1" onClick={() => setMatchModalPayment(p)}>
                              Match
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDismiss(p.id)}>
                              Dismiss
                            </Button>
                          </>
                        )}
                        {p.status === 'matched' && (
                          <small className="text-success">
                            Linked to Payment #{p.matched_payment_id}
                            {p.matched_by_agent_name && <> by {p.matched_by_agent_name}</>}
                          </small>
                        )}
                        {p.status === 'dismissed' && (
                          <Button variant="outline-warning" size="sm" onClick={() => handleDismiss(p.id)}>
                            Undo
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button variant="secondary" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>
            &larr; Previous
          </Button>
          <span className="text-muted">Page {currentPage} of {totalPages}</span>
          <Button variant="secondary" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}>
            Next &rarr;
          </Button>
        </div>
      )}

      {/* Match Modal */}
      {matchModalPayment && (
        <ReconciliationMatchModal
          show={!!matchModalPayment}
          payment={matchModalPayment}
          apiCall={apiCall}
          onHide={() => setMatchModalPayment(null)}
          onMatch={handleMatchComplete}
        />
      )}
    </>
  );
}

export default ReconciliationView;
